// ============================================================
// WishlistManager — handles guest (localStorage) and
// logged-in (App Proxy -> Worker) wishlist operations.
// Bootstrapped from window.__wishlist in theme.liquid.
// ============================================================
class WishlistManager {
  constructor() {
    this.isLoggedIn = window.__wishlist?.isLoggedIn || false;
    this.items = new Set();
    this.listeners = new Set();
    this.proxyBase = '/apps/wishlist';
    this._ready = this._init();
  }

  // --- Lifecycle ------------------------------------------------
  async _init() {
    if (this.isLoggedIn) {
      await this._initLoggedIn();
    } else {
      this._initGuest();
    }
    this._updateAllButtons();
    this._updateBadge();
  }

  _initGuest() {
    try {
      const stored = JSON.parse(localStorage.getItem('wishlist') || '[]');
      stored.forEach((id) => this.items.add(String(id)));
    } catch {
      this.items.clear();
    }
  }

  async _initLoggedIn() {
    const guestItems = this._getGuestItems();
    if (guestItems.length > 0) {
      try {
        const res = await fetch(`${this.proxyBase}/merge`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ productIds: guestItems }),
        });
        const data = await res.json();
        this._setFromGids(data.productIds || []);
        localStorage.removeItem('wishlist');
      } catch (err) {
        console.error('[Wishlist] Merge failed:', err);
        await this._fetchServerWishlist();
      }
    } else {
      await this._fetchServerWishlist();
    }
  }

  async _fetchServerWishlist() {
    try {
      const res = await fetch(`${this.proxyBase}/wishlist`);
      const data = await res.json();
      this._setFromGids(data.productIds || []);
    } catch (err) {
      console.error('[Wishlist] Fetch failed:', err);
    }
  }

  _getGuestItems() {
    try {
      return JSON.parse(localStorage.getItem('wishlist') || '[]');
    } catch {
      return [];
    }
  }

  // --- Public API -----------------------------------------------
  async toggle(productId) {
    await this._ready;
    const id = String(productId);
    if (this.isLoggedIn) {
      return this._toggleServer(id);
    }
    return this._toggleGuest(id);
  }

  has(productId) {
    return this.items.has(String(productId));
  }

  get count() {
    return this.items.size;
  }

  getAll() {
    return [...this.items];
  }

  onChange(callback) {
    this.listeners.add(callback);
    return () => this.listeners.delete(callback);
  }

  // --- Guest operations -----------------------------------------
  _toggleGuest(id) {
    const added = !this.items.has(id);
    if (added) {
      this.items.add(id);
    } else {
      this.items.delete(id);
    }
    this._saveGuest();
    this._notify(id, added);
    return { added };
  }

  _saveGuest() {
    localStorage.setItem('wishlist', JSON.stringify([...this.items]));
  }

  // --- Server operations ----------------------------------------
  async _toggleServer(id) {
    const wasInList = this.items.has(id);
    const added = !wasInList;
    if (added) {
      this.items.add(id);
    } else {
      this.items.delete(id);
    }
    this._notify(id, added);
    try {
      const res = await fetch(`${this.proxyBase}/wishlist/toggle`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ productId: id }),
      });
      if (!res.ok) {
        if (added) this.items.delete(id);
        else this.items.add(id);
        this._notify(id, !added);
        const err = await res.json();
        console.error('[Wishlist] Toggle failed:', err);
        return { added: !added, error: err.error };
      }
      const data = await res.json();
      this._setFromGids(data.productIds);
      return { added: data.added };
    } catch (err) {
      if (added) this.items.delete(id);
      else this.items.add(id);
      this._notify(id, !added);
      console.error('[Wishlist] Network error:', err);
      return { added: !added, error: 'network' };
    }
  }

  // --- Internal helpers ------------------------------------------
  _setFromGids(gids) {
    this.items.clear();
    gids.forEach((gid) => {
      const numericId = gid.replace('gid://shopify/Product/', '');
      this.items.add(numericId);
    });
  }

  _notify(productId, added) {
    this._updateAllButtons();
    this._updateBadge();
    this._showToast(added ? 'Added to Wishlist' : 'Removed from Wishlist');
    this.listeners.forEach((cb) => {
      try {
        cb(productId, added, this);
      } catch (e) {
        console.error(e);
      }
    });
  }

  _updateAllButtons() {
    document.querySelectorAll('[data-wishlist-btn]').forEach((btn) => {
      const id = btn.dataset.productId;
      const actionText = this.has(id)
        ? 'Remove from Wishlist'
        : 'Add to Wishlist';
      btn.classList.toggle('is-wishlisted', this.has(id));
      if (btn.innerText !== '') {
        btn.textContent = actionText;
      }
      btn.setAttribute('aria-label', actionText);
    });
  }

  _updateBadge() {
    const badge = document.querySelector('[data-wishlist-count]');
    if (!badge) return;
    const count = this.count;
    badge.textContent = count > 0 ? count : '';
    badge.hidden = count === 0;
  }

  _showToast(message) {
    const existing = document.querySelector('.wishlist-toast');
    if (existing) existing.remove();
    const toast = document.createElement('div');
    toast.className = 'wishlist-toast';
    toast.textContent = message;
    document.body.appendChild(toast);
    requestAnimationFrame(() => toast.classList.add('is-visible'));
    setTimeout(() => {
      toast.classList.remove('is-visible');
      setTimeout(() => toast.remove(), 300);
    }, 2000);
  }

  async _fetchProductsByIds(ids) {
    if (!Array.isArray(ids) || ids.length === 0) {
      return { products: [], staleIds: [] };
    }

    const publicStorefrontToken = '6eeee147b5a3733dc7124cc8a03d2d3d';
    const gids = ids.map((id) => `gid://shopify/Product/${id}`);

    const query = `
    query WishlistProducts($ids: [ID!]!) {
      nodes(ids: $ids) {
        ... on Product {
          id
          handle
          title
          onlineStoreUrl
          availableForSale
          featuredImage { url altText width height }
          priceRange { minVariantPrice { amount currencyCode } }
          variants(first: 1) { nodes { id availableForSale } }
        }
      }
    }`;

    const res = await fetch('/api/2025-07/graphql.json', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': publicStorefrontToken,
      },
      body: JSON.stringify({ query, variables: { ids: gids } }),
    });

    if (!res.ok) throw new Error(`Storefront API ${res.status}`);
    const { data, errors } = await res.json();
    if (errors?.length) throw new Error('Storefront API errors');

    const products = [];
    const staleIds = [];
    data.nodes.forEach((node, i) => {
      if (node) products.push(node);
      else staleIds.push(ids[i]);
    });
    return { products, staleIds };
  }

  async reconcile(staleIds) {
    await this._ready;
    if (!Array.isArray(staleIds) || staleIds.length === 0)
      return { removed: 0 };

    const toRemove = staleIds.map(String).filter((id) => this.items.has(id));
    if (toRemove.length === 0) return { removed: 0 };

    // Safety net — don't wipe entire wishlist on suspicious result
    if (toRemove.length === this.items.size) {
      console.warn('[Wishlist] All IDs reported stale — skipping reconcile');
      return { removed: 0, skipped: true };
    }

    toRemove.forEach((id) => this.items.delete(id));

    if (this.isLoggedIn) {
      await Promise.allSettled(
        toRemove.map((id) =>
          fetch(`${this.proxyBase}/wishlist/toggle`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ productId: id }),
          }),
        ),
      );
    } else {
      this._saveGuest();
    }

    this._updateAllButtons();
    this._updateBadge();
    return { removed: toRemove.length };
  }
}

// Singleton
window.wishlistManager = new WishlistManager();

// ============================================================
// Global click delegation for wishlist buttons
// ============================================================
document.addEventListener('click', async (e) => {
  const btn = e.target.closest('[data-wishlist-btn]');
  if (!btn) return;
  e.preventDefault();
  e.stopPropagation();
  const productId = btn.dataset.productId;
  if (!productId) return;
  btn.disabled = true;
  await window.wishlistManager.toggle(productId);
  btn.disabled = false;
});
